import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { getApiErrorMessage } from '@/api/admin-access';
import {
  applyImportMatch,
  type DataType,
  downloadImportReport,
  downloadUserData,
  executeDataImport,
  getCumulativeImportAiUsage,
  getDataHumidors,
  getDataImport,
  getDataSummary,
  type ImportHumidor,
  type ImportJob,
  listDataImports,
  listImportMatches,
  type MatchGroup,
  previewDataImport,
  searchImportCigars,
  skipImportMatch,
  stageDataImport,
} from '@/api/user-data-management';
import type { ManagedUserDetails } from '@/api/users';

const fields: Record<DataType, string[]> = {
  inventory: [
    'source_id',
    'humidor_id',
    'humidor_name',
    'cigar_id',
    'brand',
    'cigar_name',
    'vitola',
    'quantity',
    'date_added',
    'purchase_date',
    'purchase_price',
    'purchase_location',
    'notes',
  ],
  sessions: [
    'source_id',
    'cigar_id',
    'brand',
    'cigar_name',
    'vitola',
    'session_date',
    'rating',
    'rating_scale',
    'duration',
    'notes',
    'flavor_notes',
    'strength',
    'humidor_name',
    'buy_again',
  ],
};
const saveBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const nameOf = (user: ManagedUserDetails) =>
  user.display_name || user.full_name || `${user.first_name} ${user.last_name}`;
const statusLabel = (status: string) =>
  ({
    matched: 'Matched Automatically',
    matched_manually: 'Matched Manually',
    needs_review: 'Needs Review',
    not_found: 'Not Found',
    resolved: 'Resolved',
    skipped: 'Skipped',
    imported: 'Imported',
    invalid: 'Failed Validation',
    failed: 'Failed',
    awaiting_credits: 'Awaiting AI credits',
    researching: 'Researching cigars',
    staged: 'Queued',
    processing: 'Processing',
    ready_for_review: 'Ready for review',
  })[status] || status;
const csvHeaders = (text: string) => {
  const headers: string[] = [];
  let value = '';
  let quoted = false;
  const input = text.replace(/^\uFEFF/, '');
  for (let index = 0; index < input.length; index++) {
    const character = input[index];
    if (character === '"') {
      if (quoted && input[index + 1] === '"') {
        value += '"';
        index++;
      } else quoted = !quoted;
    } else if (
      !quoted &&
      (character === ',' || character === '\n' || character === '\r')
    ) {
      headers.push(value.trim());
      value = '';
      if (character !== ',') return headers;
    } else value += character;
  }
  headers.push(value.trim());
  return headers;
};
const safeCsvCell = (value: unknown) => {
  let text = String(value ?? '');
  if (/^\s*[=+@\-\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

export default function UserDataManagement({
  user,
}: {
  user: ManagedUserDetails;
}) {
  const userId = user.user_id;
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<Awaited<
    ReturnType<typeof getDataSummary>
  > | null>(null);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [cumulativeAiUsage, setCumulativeAiUsage] = useState<Awaited<
    ReturnType<typeof getCumulativeImportAiUsage>
  > | null>(null);
  const [type, setType] = useState<DataType>('inventory');
  const [creditClassification, setCreditClassification] = useState<
    'customer' | 'promotional'
  >('customer');
  const [humidors, setHumidors] = useState<ImportHumidor[]>([]);
  const [destinationHumidor, setDestinationHumidor] = useState('default');
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<any>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<Awaited<
    ReturnType<typeof getDataImport>
  > | null>(null);
  const [groups, setGroups] = useState<MatchGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<MatchGroup | null>(null);
  const [query, setQuery] = useState('');
  const [cigars, setCigars] = useState<
    Awaited<ReturnType<typeof searchImportCigars>>
  >([]);
  const [exportInventory, setExportInventory] = useState(true);
  const [exportSessions, setExportSessions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeModal, setActiveModal] = useState<'import' | 'export' | null>(
    null
  );
  const [error, setError] = useState('');
  const totalImportRows = jobDetail?.progress.total || 0;
  const processedImportRows = jobDetail?.progress.finished || 0;

  const refresh = useCallback(async () => {
    const [nextSummary, nextJobs, nextAiUsage] = await Promise.all([
      getDataSummary(userId),
      listDataImports(userId),
      getCumulativeImportAiUsage(userId),
    ]);
    setSummary(nextSummary);
    setJobs(nextJobs);
    setCumulativeAiUsage(nextAiUsage);
  }, [userId]);
  useEffect(() => {
    refresh().catch((err) => setError(getApiErrorMessage(err)));
  }, [refresh]);
  useEffect(() => {
    if (activeModal !== 'import' || type !== 'inventory' || jobId) return;
    getDataHumidors(userId)
      .then((items) => {
        setHumidors(items);
      })
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [activeModal, type, userId, jobId]);
  const loadJob = async (id: string) => {
    setActiveModal('import');
    setJobId(id);
    const [detail, matches] = await Promise.all([
      getDataImport(userId, id),
      listImportMatches(userId, id),
    ]);
    setJobDetail(detail);
    setGroups(matches);
  };
  useEffect(() => {
    const linkedJob = new URLSearchParams(location.search).get('importJobId');
    if (!linkedJob) return;
    getDataImport(userId, linkedJob)
      .then((detail) => {
        setJobId(linkedJob);
        setJobDetail(detail);
        setActiveModal('import');
        return listImportMatches(userId, linkedJob);
      })
      .then(setGroups)
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [location.search, userId]);
  useEffect(() => {
    if (
      activeModal !== 'import' ||
      !jobId ||
      !['staged', 'processing', 'researching'].includes(
        jobDetail?.job.status || ''
      )
    )
      return undefined;
    const timer = window.setInterval(() => {
      getDataImport(userId, jobId)
        .then((detail) => {
          setJobDetail(detail);
          if (
            !['staged', 'processing', 'researching'].includes(detail.job.status)
          ) {
            listImportMatches(userId, jobId)
              .then(setGroups)
              .catch(() => {});
            refresh().catch(() => {});
          }
        })
        .catch((err) => setError(getApiErrorMessage(err)));
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeModal, jobId, jobDetail?.job.status, userId, refresh]);
  const onFile = async (chosen?: File) => {
    setPreview(null);
    setJobId(null);
    setJobDetail(null);
    setError('');
    if (!chosen) {
      setFile(null);
      return;
    }
    if (
      !chosen.name.toLowerCase().endsWith('.csv') ||
      chosen.size > 2 * 1024 * 1024
    ) {
      setError('Select a CSV file under 2 MB.');
      return;
    }
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(
        await chosen.arrayBuffer()
      );
      if (text.includes('\0'))
        throw new Error('The CSV contains invalid text.');
      setFile({ name: chosen.name, csv: text });
      const guessed = csvHeaders(text);
      setHeaders(guessed);
      setMapping(
        Object.fromEntries(
          guessed
            .filter((h) => fields[type].includes(h.toLowerCase()))
            .map((h) => [h, h.toLowerCase()])
        )
      );
    } catch (err) {
      setError((err as Error).message || 'Could not read the CSV as UTF-8.');
    }
  };
  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await task();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  let selectedDestination:
    | { mode: 'default' }
    | { mode: 'new' }
    | { mode: 'existing'; humidorId: number };
  if (destinationHumidor === 'default') {
    selectedDestination = { mode: 'default' };
  } else if (destinationHumidor === 'new') {
    selectedDestination = { mode: 'new' };
  } else {
    selectedDestination = {
      mode: 'existing',
      humidorId: Number(destinationHumidor),
    };
  }
  const payload = file && {
    dataType: type,
    filename: file.name,
    csv: file.csv,
    mapping,
    creditClassification,
    ...(type === 'inventory' ? { destination: selectedDestination } : {}),
  };
  const count = (status: string) =>
    jobDetail?.counts.find((item) => item.status === status)?.count || 0;

  return (
    <>
      <Card style={{ marginTop: 16, padding: 20 }}>
        <h5>Data Management</h5>
        <p>
          <strong>Target account:</strong> {nameOf(user)} · {user.email} · ID{' '}
          {userId} · {user.active ? 'Active' : 'Inactive'}
        </p>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <h6>Data summary</h6>
        {summary ? (
          <p>
            {summary.humidors} humidors · {summary.inventory_records} inventory
            records · {summary.total_quantity} cigars · {summary.sessions} smoke
            sessions
          </p>
        ) : (
          <p>Loading summary…</p>
        )}
        <hr />
        <h6>AI costs</h6>
        <p className="mb-1">
          {cumulativeAiUsage?.requests || 0} requests · $
          {Number(cumulativeAiUsage?.total_cost_usd || 0).toFixed(4)}
        </p>
        <small className="text-muted">
          AI credits consumed: {Number(cumulativeAiUsage?.credits_charged || 0)}
        </small>
        {cumulativeAiUsage?.estimated_requests ? (
          <div>
            <small className="text-muted">Includes estimated costs</small>
          </div>
        ) : null}
        <hr />
        <h6>Import history</h6>
        {jobs.length ? (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {jobs.map((job) => (
              <div key={job.id} className="border-bottom py-2">
                <div
                  className="font-weight-bold"
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {job.filename}
                </div>
                <small className="text-muted">
                  {new Date(job.created_at).toLocaleString()} · {job.data_type}{' '}
                  · {statusLabel(job.status)}
                </small>
                <div>
                  <small>
                    {job.unresolved || 0} unresolved · AI $
                    {Number(job.ai_usage?.total_cost_usd || 0).toFixed(4)}
                  </small>
                </div>
                <Button
                  size="sm"
                  color="link"
                  className="p-0"
                  onClick={() => run(() => loadJob(job.id))}
                >
                  Review matches / report
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">No imports yet.</p>
        )}
        <div className="d-flex mt-3" style={{ gap: 8 }}>
          <Button
            color="primary"
            onClick={() => {
              setError('');
              setJobId(null);
              setJobDetail(null);
              setActiveModal('import');
            }}
          >
            Import data
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setError('');
              setActiveModal('export');
            }}
          >
            Export data
          </Button>
        </div>
      </Card>
      <Modal
        isOpen={activeModal === 'import'}
        toggle={() => !busy && setActiveModal(null)}
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => !busy && setActiveModal(null)}>
          Import data for {nameOf(user)}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {user.email} · User ID {userId}
          </p>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <h6>Import data</h6>
          <p>
            CSV only. Upload inventory and smoke sessions as separate files.
            Unmatched cigars remain pending until reviewed.
          </p>
          <div className="form-group mb-3">
            <label htmlFor="data-type">Data type</label>
            <select
              id="data-type"
              className="form-control"
              value={type}
              onChange={(event) => {
                setType(event.target.value as DataType);
                setFile(null);
                setPreview(null);
              }}
            >
              <option value="inventory">Humidor inventory</option>
              <option value="sessions">Smoke sessions / journal</option>
            </select>
          </div>
          {type === 'inventory' && !jobId && (
            <div className="form-group mb-3">
              <label htmlFor="import-humidor">Destination humidor</label>
              <select
                id="import-humidor"
                className="form-control"
                value={destinationHumidor}
                onChange={(event) => setDestinationHumidor(event.target.value)}
              >
                <option value="default">
                  Default humidor
                  {humidors.find((humidor) => humidor.is_default)?.name
                    ? ` (${humidors.find((humidor) => humidor.is_default)?.name})`
                    : ''}
                </option>
                {humidors.map((humidor) => (
                  <option key={humidor.id} value={humidor.id}>
                    {humidor.name}
                    {humidor.is_default ? ' (default)' : ''}
                  </option>
                ))}
                <option value="new">
                  Create New Humidor (Imported Humidor)
                </option>
              </select>
            </div>
          )}
          <div className="form-group mb-3">
            <label htmlFor="data-file">Source CSV</label>
            <input
              id="data-file"
              type="file"
              accept=".csv,text/csv"
              className="form-control"
              onChange={(event) => onFile(event.target.files?.[0])}
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="import-credit-classification">
              AI credit source
            </label>
            <select
              id="import-credit-classification"
              className="form-control"
              value={creditClassification}
              onChange={(event) =>
                setCreditClassification(
                  event.target.value as 'customer' | 'promotional'
                )
              }
            >
              <option value="customer">Customer credits</option>
              <option value="promotional">
                Complimentary promotional credits
              </option>
            </select>
            <small>
              Only AI research consumes credits. Confirm that complimentary
              credits have been allocated before selecting promotional.
            </small>
          </div>
          {file && (
            <>
              <h6>Field mapping</h6>
              <p>
                Map CSV columns to Boxpressd fields. Unmapped columns remain in
                the saved source record.
              </p>
              {headers.map((header) => (
                <div key={header} className="row mb-2">
                  <label className="col-sm-5" htmlFor={`map-${header}`}>
                    {header}
                  </label>
                  <div className="col-sm-7">
                    <select
                      id={`map-${header}`}
                      className="form-control"
                      value={mapping[header] || ''}
                      onChange={(event) => {
                        setMapping((current) => ({
                          ...current,
                          [header]: event.target.value,
                        }));
                        setPreview(null);
                      }}
                    >
                      <option value="">Do not map</option>
                      {fields[type].map((field) => (
                        <option value={field} key={field}>
                          {field.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <Button
                color="secondary"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    setPreview(await previewDataImport(userId, payload!));
                  })
                }
              >
                Preview / dry run
              </Button>
            </>
          )}
          {preview && (
            <div className="mt-3">
              <h6>Dry run</h6>
              <p>
                {preview.total} source records · {preview.valid} valid ·{' '}
                {preview.invalid} invalid ·{' '}
                {preview.matchesAreSample ? 'First 30 rows: ' : ''}
                {preview.matches?.matched || 0} matched ·{' '}
                {preview.matches?.needs_review || 0} needs review ·{' '}
                {preview.matches?.not_found || 0} not found
              </p>
              <p>
                {preview.matchesAreSample ? 'Sample' : 'Estimated'} records to
                create: {preview.recordsToCreate || 0} · to update: 0 · to skip:{' '}
                {preview.recordsToSkip || 0}. Existing exported records may be
                skipped during execution.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Cigar</th>
                      <th>Quantity / rating</th>
                      <th>Match</th>
                      <th>Suggested cigar ID</th>
                      <th>Suggested vitola ID</th>
                      <th>Confidence</th>
                      <th>Catalog evidence</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row: any) => (
                      <tr key={row.row}>
                        <td>{row.row}</td>
                        <td>
                          {row.data.brand} {row.data.cigar_name}
                        </td>
                        <td>
                          {row.data.quantity ??
                            row.data.normalized_rating ??
                            ''}
                        </td>
                        <td>{row.cigarMatch?.status || ''}</td>
                        <td>
                          {row.cigarMatch?.suggestedId ? (
                            <span
                              title={
                                row.cigarMatch.suggestedCigarFullName || ''
                              }
                              style={{
                                cursor: 'help',
                                textDecoration: 'underline dotted',
                              }}
                            >
                              {row.cigarMatch.suggestedId}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.cigarMatch?.vitolaId ? (
                            <span
                              title={
                                row.cigarMatch.suggestedVitolaFormattedName ||
                                ''
                              }
                              style={{
                                cursor: 'help',
                                textDecoration: 'underline dotted',
                              }}
                            >
                              {row.cigarMatch.vitolaId}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.cigarMatch?.confidence == null
                            ? '—'
                            : `${Math.round(row.cigarMatch.confidence * 100)}%`}
                        </td>
                        <td>
                          {row.cigarMatch?.matchSource ||
                            row.cigarMatch?.reviewReason ||
                            '—'}
                          {row.cigarMatch?.candidateNames?.length ? (
                            <span>
                              {' · Candidate IDs: '}
                              {row.cigarMatch.candidateNames.map(
                                (candidate: any, index: number) => (
                                  <React.Fragment key={candidate.id}>
                                    {index > 0 ? ', ' : ''}
                                    <span
                                      title={candidate.fullName}
                                      style={{
                                        cursor: 'help',
                                        textDecoration: 'underline dotted',
                                      }}
                                    >
                                      {candidate.id}
                                    </span>
                                  </React.Fragment>
                                )
                              )}
                            </span>
                          ) : (
                            ''
                          )}
                        </td>
                        <td>{row.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.exceptions?.length > 0 && (
                <Button
                  color="secondary"
                  className="mr-2"
                  onClick={() => {
                    const reportColumns = [
                      'row',
                      'brand',
                      'cigar_name',
                      'errors',
                    ];
                    const lines = preview.exceptions.map(
                      (entry: Record<string, string>) =>
                        reportColumns
                          .map((header) => safeCsvCell(entry[header]))
                          .join(',')
                    );
                    saveBlob(
                      new Blob(
                        [
                          `\uFEFF${reportColumns.join(',')}\r\n${lines.join('\r\n')}`,
                        ],
                        { type: 'text/csv;charset=utf-8' }
                      ),
                      `boxpressd-import-exceptions-${userId}.csv`
                    );
                  }}
                >
                  Download exception report
                </Button>
              )}
              <Button
                color="primary"
                disabled={busy || !preview.valid}
                onClick={() =>
                  run(async () => {
                    if (
                      !window.confirm(
                        `Start importing high-confidence matches into ${nameOf(user)} (${user.email}, ID ${userId})?`
                      )
                    )
                      return;
                    const staged = await stageDataImport(userId, payload!);
                    await loadJob(staged.jobId);
                    await refresh();
                  })
                }
              >
                Start import for {nameOf(user)}
              </Button>
            </div>
          )}
          {jobDetail && (
            <div className="mt-4">
              <h6>Import {jobDetail.job.id}</h6>
              {['staged', 'processing', 'researching'].includes(
                jobDetail.job.status
              ) && (
                <div className="mb-3">
                  <div
                    className="progress"
                    role="progressbar"
                    aria-label="Import progress"
                    aria-valuenow={processedImportRows}
                    aria-valuemin={0}
                    aria-valuemax={totalImportRows}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${Math.round((100 * processedImportRows) / Math.max(1, totalImportRows))}%`,
                      }}
                    />
                  </div>
                  <small>
                    {processedImportRows} of {totalImportRows} rows processed.
                    You can leave this page.
                  </small>
                </div>
              )}
              <p>
                {jobDetail.progress.deterministic_imported} imported without AI
                · {jobDetail.progress.ai_imported} imported using AI ·{' '}
                {jobDetail.progress.research_pending} researching ·{' '}
                {jobDetail.progress.awaiting_credits} awaiting credits
              </p>
              <p>
                Status: {statusLabel(jobDetail.job.status)} ·{' '}
                {jobDetail.counts
                  .map((c) => `${statusLabel(c.status)}: ${c.count}`)
                  .join(' · ')}
                {jobDetail.job.completed_at &&
                  ` · Completed ${new Date(jobDetail.job.completed_at).toLocaleString()}`}
              </p>
              <div className="alert alert-light">
                <strong>AI Usage Summary</strong>
                <div>AI Requests: {jobDetail.ai_usage.requests}</div>
                <div>
                  Input Tokens:{' '}
                  {Number(jobDetail.ai_usage.input_tokens).toLocaleString()}
                </div>
                <div>
                  Output Tokens:{' '}
                  {Number(jobDetail.ai_usage.output_tokens).toLocaleString()}
                </div>
                <div>
                  Cached Input Tokens:{' '}
                  {Number(
                    jobDetail.ai_usage.cached_input_tokens
                  ).toLocaleString()}
                </div>
                <div>
                  Total AI Cost: $
                  {Number(jobDetail.ai_usage.total_cost_usd).toFixed(4)}
                  {jobDetail.ai_usage.estimated_requests
                    ? ' (includes estimates)'
                    : ''}
                </div>
                <div>Billing: Boxpressd · User Credits Charged: 0</div>
                {jobDetail.ai_usage.breakdown?.map((item) => (
                  <div key={`${item.operation}-${item.provider}-${item.model}`}>
                    {item.operation.replace(/_/g, ' ')} · {item.provider} /{' '}
                    {item.model}: {item.requests} requests, $
                    {Number(item.total_cost_usd).toFixed(4)}
                    {item.estimated_requests ? ' estimated in part' : ''}
                  </div>
                ))}
              </div>
              {jobDetail.job.completed_at && (
                <div className="alert alert-info">
                  <strong>Import Complete</strong>
                  <br />
                  Successfully imported: {count('imported') + count('resolved')}
                  <br />
                  Requires cigar matching:{' '}
                  {count('needs_review') +
                    count('not_found') +
                    count('skipped')}
                  <br />
                  Failed validation: {count('invalid')}
                </div>
              )}
              <p>
                Review the staged rows before writing data to {nameOf(user)}.
              </p>
              <div style={{ maxHeight: 250, overflow: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Cigar</th>
                      <th>Status</th>
                      <th>Record ID</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobDetail.rows.map((row) => (
                      <tr key={row.source_key}>
                        <td>{row.row_number}</td>
                        <td>
                          {row.normalized.brand} {row.normalized.cigar_name}
                        </td>
                        <td>{statusLabel(row.status)}</td>
                        <td>{row.record_id || ''}</td>
                        <td>{row.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                color="primary"
                disabled={
                  busy || !(count('matched') + count('matched_manually'))
                }
                onClick={() =>
                  run(async () => {
                    if (
                      !window.confirm(
                        `Import confirmed matches into ${nameOf(user)} (${user.email}, ID ${userId})?`
                      )
                    )
                      return;
                    let result;
                    do {
                      // Each request commits at most 50 rows; continue until the staged job is drained.
                      // eslint-disable-next-line no-await-in-loop
                      result = await executeDataImport(userId, jobId!);
                    } while (result.remaining > 0);
                    await loadJob(jobId!);
                    await refresh();
                  })
                }
              >
                Confirm and import matched records
              </Button>{' '}
              {count('failed') > 0 && (
                <Button
                  color="warning"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await executeDataImport(userId, jobId!, true);
                      await loadJob(jobId!);
                      await refresh();
                    })
                  }
                >
                  Retry failed records
                </Button>
              )}{' '}
              {(
                [
                  ['all', 'All results'],
                  ['imported', 'Imported'],
                  ['review', 'Needs review'],
                  ['unmatched', 'Unmatched and review'],
                  ['skipped', 'Skipped'],
                  ['failed', 'Failed'],
                  ['pending', 'Pending'],
                  ['unresolved', 'Unresolved and failed'],
                ] as const
              ).map(([status, label]) => (
                <Button
                  key={status}
                  color="secondary"
                  className="mr-2 mb-2"
                  disabled={busy}
                  onClick={() =>
                    run(async () =>
                      saveBlob(
                        await downloadImportReport(userId, jobId!, status),
                        `boxpressd-import-${jobId}-${status}.csv`
                      )
                    )
                  }
                >
                  Download {label.toLowerCase()}
                </Button>
              ))}
              {jobDetail.reconciliation.total_groups > 0 && (
                <p>
                  {jobDetail.reconciliation.reviewed_groups} of{' '}
                  {jobDetail.reconciliation.total_groups} cigar groups reviewed.
                </p>
              )}
              {groups.length > 0 && (
                <>
                  <h6 className="mt-4">
                    Review unmatched cigars ({groups.length} groups)
                  </h6>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Original brand</th>
                          <th>Cigar</th>
                          <th>Vitola</th>
                          <th>Suggested ID</th>
                          <th>Confidence</th>
                          <th>Affected</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((group) => (
                          <tr key={group.group_key}>
                            <td>{group.brand}</td>
                            <td>{group.cigar_name}</td>
                            <td>{group.vitola}</td>
                            <td>{group.suggested_cigar_id || '—'}</td>
                            <td>
                              {group.confidence === null
                                ? '—'
                                : `${Math.round(group.confidence * 100)}%`}
                            </td>
                            <td>{group.affected_records}</td>
                            <td>{statusLabel(group.status)}</td>
                            <td>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedGroup(group);
                                  setQuery(
                                    `${group.brand} ${group.cigar_name}`.trim()
                                  );
                                  setCigars([]);
                                }}
                              >
                                Search / select
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {selectedGroup && (
                <div className="mt-3">
                  <h6>
                    Match {selectedGroup.brand} {selectedGroup.cigar_name}{' '}
                    {selectedGroup.vitola}
                  </h6>
                  <input
                    className="form-control mb-2"
                    aria-label="Search cigars"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  <Button
                    size="sm"
                    onClick={() =>
                      run(async () =>
                        setCigars(await searchImportCigars(userId, query))
                      )
                    }
                  >
                    Search cigars
                  </Button>{' '}
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() =>
                      navigate('/cigars/editor/new', {
                        state: {
                          importSource: {
                            brand: selectedGroup.brand,
                            name: selectedGroup.cigar_name,
                            vitola: selectedGroup.vitola,
                          },
                        },
                      })
                    }
                  >
                    Create missing cigar
                  </Button>{' '}
                  <Button
                    size="sm"
                    color="link"
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        await skipImportMatch(
                          userId,
                          jobId!,
                          selectedGroup.group_key
                        );
                        setSelectedGroup(null);
                        await loadJob(jobId!);
                        await refresh();
                      })
                    }
                  >
                    Skip / leave pending
                  </Button>
                  {cigars.map((cigar) => (
                    <div
                      key={cigar.cigar_id}
                      className="border rounded p-2 mt-2"
                    >
                      {cigar.image_url && (
                        <img
                          src={cigar.image_url}
                          alt=""
                          width={48}
                          height={48}
                          style={{ objectFit: 'contain', marginRight: 8 }}
                        />
                      )}
                      <strong>
                        {cigar.brand} · {cigar.name}
                      </strong>{' '}
                      · ID {cigar.cigar_id} ·{' '}
                      {cigar.wrapper || 'Wrapper unknown'} ·{' '}
                      {cigar.origin || 'Origin unknown'} ·{' '}
                      {cigar.vitolas || 'Vitola unknown'}{' '}
                      <Button
                        size="sm"
                        color="primary"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            const matched = await applyImportMatch(
                              userId,
                              jobId!,
                              selectedGroup.group_key,
                              cigar.cigar_id
                            );
                            for (const affectedJobId of matched.affectedJobIds ||
                              []) {
                              let batch;
                              do {
                                // Reconciliation may span separate inventory and session jobs.
                                // eslint-disable-next-line no-await-in-loop
                                batch = await executeDataImport(
                                  userId,
                                  affectedJobId
                                );
                              } while (batch.remaining > 0);
                            }
                            setSelectedGroup(null);
                            await loadJob(jobId!);
                            await refresh();
                          })
                        }
                      >
                        Select match and apply
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={busy}
            onClick={() => setActiveModal(null)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={activeModal === 'export'}
        toggle={() => !busy && setActiveModal(null)}
      >
        <ModalHeader toggle={() => !busy && setActiveModal(null)}>
          Export data for {nameOf(user)}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {user.email} · User ID {userId}
          </p>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <h6>Export data</h6>
          <p>
            Exports are generated for {nameOf(user)} and downloaded directly to
            this browser.
          </p>
          <label className="mr-3">
            <input
              type="checkbox"
              checked={exportInventory}
              onChange={(event) => setExportInventory(event.target.checked)}
            />{' '}
            Humidor inventory
          </label>{' '}
          <label>
            <input
              type="checkbox"
              checked={exportSessions}
              onChange={(event) => setExportSessions(event.target.checked)}
            />{' '}
            Smoke sessions
          </label>
          <br />
          <Button
            color="secondary"
            disabled={busy || (!exportInventory && !exportSessions)}
            onClick={() =>
              run(async () =>
                saveBlob(
                  await downloadUserData(
                    userId,
                    exportInventory,
                    exportSessions
                  ),
                  `boxpressd-user-${userId}-export.zip`
                )
              )
            }
          >
            Export selected data
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={busy}
            onClick={() => setActiveModal(null)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
